<script setup lang="ts">
  import { message } from "ant-design-vue";
  import enUS from "ant-design-vue/es/locale/en_US";
  import zhCN from "ant-design-vue/es/locale/zh_CN";
  import dayjs from "dayjs";
  import "dayjs/locale/zh-cn";
  import moment from "moment";
  import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from "vue";
  import jigouIcon from "~/assets/icons/deli-module/jigou.svg";
  import svipIcon from "~/assets/icons/deli-module/svip.svg";
  import defaultAvatar from "~/assets/icons/deli-module/user-icon.png";
  import vipIcon from "~/assets/icons/deli-module/vip.svg";
  import { useNumAnimation } from "~/composables/useNumAnimation";
  import { useCountdown } from "~/composables/useTimeDown";
  import { LOCAL_STORAGE_KEY } from "~/enum/cache/localStorage";
  import { API_URL, DATA_URL, PROTOCOL_URL } from "~/env";
  import { LOGIN_URL } from "~/env";
  import { useMainStore } from "~/stores";
  import Cookie from "~/utils/cookie";
  import Logger from "~/utils/logger";
  import Storage from "~/utils/storage";

  // import CouponModal from "./CouponModal.vue";

  const props = withDefaults(
    defineProps<{
      open: boolean; // 是否显示弹窗
      defaultType?: string; // 默认的套餐类型
      defaultPlanId?: string; // 默认的套餐ID
    }>(),
    {
      open: false,
      defaultType: "",
      defaultPlanId: "",
    },
  );

  // 用户信息

  const emit = defineEmits(["update:open", "success"]);
  const locale = ref(zhCN.locale);

  const mainStore = useMainStore();

  const instance = getCurrentInstance()?.proxy as any;

  const qrCodeUrl = ref(""); // 二维码地址
  const type = ref(props.defaultType || "VIP"); // 套餐类型（VIP：法搜vip套餐，SVIP：法搜svip套餐）
  const timer = ref<NodeJS.Timeout>(); // 定时器ID
  const orderTimer = ref<NodeJS.Timeout>(); // 订单定时器ID
  const licenseNum = ref(1); // 购买团购人数
  const planId = ref(props.defaultPlanId); // 当前套餐类型
  const planList = ref<any[]>([]); // 套餐列表
  const planInfo = ref<any>({}); // 当前套餐信息
  const orderInfo = ref<any>(null); // 当前订单信息
  const confirmModal = ref(false); // 确认弹窗的显示/隐藏
  const qrcodeLoading = ref(true); // 二维码加载状态
  const expireInfo = ref<any>(null); // 到期信息
  const userInfo = ref<any>(null); // 当前 tab 的背景图片
  const currentTabBg = computed(() => {
    return (
      type.value === "SVIP" ? svipIcon
      : type.value === "VIP" ? vipIcon
      : jigouIcon
    );
  });

  // 现有 VIP 到期时间
  const expireDate = computed(() => {
    if (mainStore.versionType === 1) {
      if (expireInfo.value.permanentLicense) {
        return `得理法搜终身会员：永久有效`;
      } else if (expireInfo.value.cvipStatus === 1) {
        return `得理法搜企业会员：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.svipStatus === 1) {
        return `得理法搜SVIP：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.vipStatus === 1) {
        return `得理法搜VIP：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.tvipStatus) {
        return `得理法搜试用会员：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.cvipStatus === 2) {
        return `得理法搜企业会员：已到期${dayjs().diff(dayjs(expireInfo.value.expireDate), "day")}天`;
      } else if (expireInfo.value.svipStatus === 2) {
        return `得理法搜SVIP：已到期${dayjs().diff(dayjs(expireInfo.value.expireDate), "day")}天`;
      } else if (expireInfo.value.vipStatus === 2) {
        return `得理法搜VIP：已到期${dayjs().diff(dayjs(expireInfo.value.expireDate), "day")}天`;
      } else {
        return "暂未开通会员";
      }
    } else {
      if (expireInfo.value.cvipStatus === 1) {
        return `得理法搜企业会员：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.permanentLicense) {
        return `得理法搜终身会员：永久有效`;
      } else if (expireInfo.value.svipStatus === 1) {
        return `得理法搜SVIP：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.vipStatus === 1) {
        return `得理法搜VIP：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.tvipStatus) {
        return `得理法搜试用会员：${expireInfo.value.expireDate.split(" ")[0]}到期`;
      } else if (expireInfo.value.cvipStatus === 2) {
        return `得理法搜企业会员：已到期${dayjs().diff(dayjs(expireInfo.value.expireDate), "day")}天`;
      } else if (expireInfo.value.svipStatus === 2) {
        return `得理法搜SVIP：已到期${dayjs().diff(dayjs(expireInfo.value.expireDate), "day")}天`;
      } else if (expireInfo.value.vipStatus === 2) {
        return `得理法搜VIP：已到期${dayjs().diff(dayjs(expireInfo.value.expireDate), "day")}天`;
      } else {
        return "暂未开通会员";
      }
    }
  });
  // 当前套餐原价
  const sourcePrice = computed(() => {
    return planList.value.find((item) => item.planId === planId.value)?.sourcePrice;
  });

  // 获取用户信息
  const loadUserInfo = async () => {
    if (!mainStore.login) return;
    const info = await instance.$Request.get(`${DATA_URL}/api/user/info`).catch((e: any) => console.error(e));
    if (!props.defaultType && !props.defaultPlanId) {
      const globalPayType = info.globalPayType;
      // globalPayType:
      // 1 - 购买vip的用户
      // 2 - 购买svip的用户
      // 3 - 没有购买的用户
      type.value = globalPayType === 1 || globalPayType === 3 ? "VIP" : "SVIP";
      planId.value = globalPayType === 1 || globalPayType === 3 ? "p" : "k";
    }
    userInfo.value = info;
  };
  // 获取到期信息
  const loadExpireInfo = () => {
    if (!mainStore.login) return;
    instance.$Request.get(`${DATA_URL}/api/v2/pay/checkExpire`).then((res: any) => {
      expireInfo.value = res;
    });
  };
  // 获取法搜套餐
  const loadPlanList = async () => {
    const params = { type: type.value };
    await instance.$Request.get(`${DATA_URL}/api/v2/pay/getPlanList`, params).then((res: any) => {
      if (!planId.value) planId.value = res[0].planId;
      planList.value = res;
      planList.value.forEach((plan: any) => {
        if (plan?.couponTime) {
          const { startRough } = useCountdown({
            totalTime: plan.couponTime,
            update: (value) => {
              plan.countdown = `${value.h}:${value.m}:${value.s}`;
              planList.value = [...planList.value];
            },
            callback: () => {},
          });
          startRough();
        }
      });
    });
  };

  const currentPlan = computed(() => {
    return planList.value.find((item: any) => item.planId === planId.value);
  });

  // 更换套餐
  const onChangePlanId = (id: string) => {
    const logMap: any = {
      a: "法搜_VIP_1个月",
      b: "法搜_VIP_连续包月",
      c: "法搜_VIP_1年",
      p: "法搜_VIP_3年",
      l: "法搜_SVIP_3年",
      m: "法搜_SVIP_1年",
      k: "法搜_SVIP_连续包月",
      n: "法搜_SVIP_1个月",
    };
    Logger.event(logMap[id]);
    planId.value = id;
    licenseNum.value = 1;
    qrcodeLoading.value = true;
    loadPlanInfo();
  };
  const autoRefreshOrder = ref(true);
  const qrcodeStatus = ref("active");

  // 获取当前套餐信息
  const loadPlanInfo = () => {
    if (!userInfo.value) return;
    qrcodeStatus.value = "active";
    const params = { planId: planId.value, licenseNum: licenseNum.value };
    instance.$Request.post(`${DATA_URL}/api/v2/pay/computeSetMealFee`, params).then((res: any) => {
      planInfo.value = res;
    });
    loadOrder();
    clearInterval(orderTimer.value);
    orderTimer.value = setInterval(
      () => {
        if (autoRefreshOrder.value) {
          autoRefreshOrder.value = false;
          loadOrder();
        } else {
          clearInterval(orderTimer.value);
          qrcodeStatus.value = "expired";
        }
      },
      3 * 60 * 1000,
    );
  };
  const currentPayNo = ref("");
  // 加载订单号
  const loadOrder = async () => {
    qrcodeStatus.value = "active";
    const params = {
      planId: planId.value,
      industry: type.value,
      licenseNum: licenseNum.value,
      source: "portal_pay_order",
    };
    // 生成订单号
    currentPayNo.value = await instance.$Request
      .post(`${DATA_URL}/api/v2/pay/buySetMeal`, params)
      .catch((e: any) => console.error(e));
    // 生成用户标识
    const { id } = await instance.$Request
      .post(`${DATA_URL}/api/v1/pay/getSign`, { payNo: currentPayNo.value })
      .catch((err: any) => {
        message.warning(err, 3);
      });
    qrCodeUrl.value = `${DATA_URL}/app/pay/superOrderPay/${currentPayNo.value}/${id}`;
    qrcodeLoading.value = false;
    getOrderInfo();
    clearInterval(timer.value);
    timer.value = setInterval(() => {
      getOrderInfo();
    }, 3000);
  };
  // 获取订单信息
  const getOrderInfo = () => {
    if (!currentPayNo.value) return;
    const params = { payNo: currentPayNo.value };
    instance.$Request.get(`${DATA_URL}/api/v2/pay/getOrderInfo`, params).then((res: any) => {
      orderInfo.value = res;
      if (res.paySuccess) {
        confirmModal.value = false;
        clearInterval(timer.value);
        clearInterval(orderTimer.value);
      }
    });
  };
  // 关闭弹窗
  const handleCancel = () => {
    if (Cookie.get("DATA_VIP_MODAL_LOGIN")) {
      Cookie.remove("DATA_VIP_MODAL_LOGIN");
    }
    if (!userInfo.value) {
      return emit("update:open", false);
    }
    const isMajor = mainStore.versionType === 1; //专业版
    //优先展示新用户挽留弹窗
    if (shouldRetainPopup() && isMajor && hasCoupon.value) {
      showRetainPopup.value = true;
      localStorage.setItem("popupLastShown", new Date().toDateString());
      if (registerDay.value > 3 && registerDay.value < 31) {
        Logger.event("法搜_首单加赠_挽留弹窗_触发次数");
        Logger.event("首购50元优惠挽留弹窗_弹出弹窗");
      } else {
        Logger.event("法搜_首单现金券_挽留弹窗_触发次数");
        Logger.event("首购加赠时长弹窗_弹出弹窗");
      }
      return;
    }
    if (hasCoupon.value) {
      return emit("update:open", false);
    }
    // 如果之前已经确认过，则不需要再次弹窗
    const isConfirm = Storage.get(LOCAL_STORAGE_KEY);
    const { sourcePrice } = planList.value.find((item: any) => item.planId === planId.value);
    if (orderInfo.value.paySuccess || !sourcePrice || type.value === "JIGOU" || isConfirm)
      return orderInfo.value.paySuccess ? window.location.reload() : emit("update:open", false);
    confirmModal.value = true;
  };

  //新用户挽留弹窗
  const showRetainPopup = ref(false);
  const shouldRetainPopup = () => {
    const lastShownDate = localStorage.getItem("popupLastShown");
    const today = new Date().toDateString();

    // 如果没有记录或记录的不是今天，就需要显示
    return !lastShownDate || lastShownDate !== today;
  };

  const closeRetainPopup = () => {
    showRetainPopup.value = false;
  };
  const closeRetain = () => {
    emit("update:open", false);
  };

  // 继续购买
  const handleKeep = () => {
    confirmModal.value = false;
    Storage.set(LOCAL_STORAGE_KEY, "1");
  };
  // 放弃特权
  const handleLeave = () => {
    Storage.set(LOCAL_STORAGE_KEY, "1");
    confirmModal.value = false;
    orderInfo.value.paySuccess ? window.location.reload() : emit("update:open", false);
  };
  // 处理添加人数
  const handleAddLicenseNum = () => {
    if (licenseNum.value >= 20) return message.warning("请咨询客服购买！");
    licenseNum.value++;
    loadPlanInfo();
  };
  // 处理减少人数
  const handleReduceLicenseNum = () => {
    if (licenseNum.value <= 1) return message.warning("人数不能小于1！");
    licenseNum.value--;
    loadPlanInfo();
  };
  // 开发票
  const handleOpenInvoice = () => {
    window.open(`${DATA_URL}/account/order`, "_blank");
  };
  // 兑换
  const handleExchange = () => {
    window.open(`${DATA_URL}?invitationCodeTable=show`, "_blank");
  };
  // 更换套餐类型
  const onChangeType = async (t: string) => {
    const logMap: any = {
      VIP: "法搜_vip",
      SVIP: "法搜_svip",
      JIGOU: "法搜_付费_机构版",
    };
    Logger.event(logMap[t]);
    type.value = t;
    licenseNum.value = 1;
    if (type.value !== "JIGOU") {
      qrcodeLoading.value = true;
      planId.value = "";
      await loadPlanList();
      loadPlanInfo();
      onDiscount();
    }
  };
  // 格式化时间
  const formatTime = (value: any, format = "YYYYMMDD") => {
    return moment(value).format(format);
  };
  // 格式化金额
  const formatMoney = (value: any) => {
    return (value / 100).toFixed(2);
  };
  // 格式化付款方式
  const formatPayment = (value: any) => {
    const PaymentMap: any = {
      wechat: "微信支付",
      alipay: "支付宝",
    };
    return PaymentMap[value];
  };
  const handleChangeLicenseNum = () => {
    if (licenseNum.value < 1) message.warning("人数不能小于1!");
    if (licenseNum.value > 20) message.warning("请咨询客服购买!");
    licenseNum.value = Math.min(20, Math.max(1, parseInt(licenseNum.value.toString()) || 1));
    loadPlanInfo();
  };

  const licenseNumInputRef = ref();

  const toLogin = () => {
    Cookie.set("DATA_VIP_MODAL_LOGIN", "true");
    location.replace(`${LOGIN_URL}&redirect=${encodeURIComponent(location.href)}`);
  };

  const handleCustomerServiceHover = () => {
    Logger.event("法搜_付费页_客服_触发次数");
  };

  const registerDay = computed(() => {
    return mainStore.info.registerDay || 0;
  });

  const showCouponPopup = ref(false);
  const ModalConfig = reactive<Record<string, any>>({});
  const ModalType = {
    TRIAL: 0,
    DISCOUNT: 1,
    BONUS: 2,
  };

  const hasCoupon = computed(() => {
    return mainStore.hasCoupon;
  });

  // const hasCoupon = ref(false);

  const loadCouponPopup = async () => {
    if (!mainStore.login) return;
    const isMajor = mainStore.versionType === 1;
    if (!isMajor) return;
    const params = {
      platform: 1,
      position: "pay",
    };
    const result = await instance.$Request.get(`${API_URL}/api/v1/promotion/queryPlatformCoupon`, params);
    if (result?.length > 0) {
      mainStore.hasCoupon = true;
      const { expireTime, amount, pictureUrl, type } = result[0];
      Object.assign(ModalConfig, {
        expireTime,
        amount,
        pictureUrl,
        type,
      });
      if (type === ModalType.DISCOUNT) {
        Logger.event("法搜_首单现金券_发放弹窗_触发次数");
        Logger.event("首购50元优惠弹窗_弹出弹窗");
      } else if (type === ModalType.BONUS) {
        Logger.event("法搜_首单加赠_发放弹窗_触发次数");
        Logger.event("首购加赠时长弹窗_弹出弹窗");
      }
      showCouponPopup.value = true;
    }
  };

  //价格变化动效
  const onDiscount = () => {
    planList.value?.forEach((item: any) => {
      if (item.couponPrice) {
        const run = useNumAnimation({
          from: item.couponPrice,
          to: item.planPrice,
          duration: 1000,
          easing: (t) => t * t * (3 - 2 * t), // 三次贝塞尔曲线
          onUpdate: (value: number) => {
            if (value > 100) {
              value = parseInt((value / 100).toFixed(0)) * 100;
            }

            item.planPrice = value;
          },
        });
        run();
      }
    });
  };

  // const onCloseCoupon = () => {
  //   onDiscount();
  // };

  onMounted(async () => {
    await loadUserInfo();
    loadExpireInfo();
    await loadPlanList();
    loadPlanInfo();
    loadCouponPopup();
  });

  onBeforeUnmount(() => {
    clearInterval(timer.value);
    clearInterval(orderTimer.value);
  });
</script>

<template>
  <a-modal
    wrap-class-name="x-data-vip-modal"
    :open="open"
    :keyboard="false"
    :mask-closable="false"
    :footer="null"
    @cancel="handleCancel">
    <template #closeIcon>
      <!-- <Icon-font type="module-a-Slice267" /> -->
      <icon-font type="Slice267-hn66e9an" />
      <!-- <img class="tw-h-[24px] tw-w-[24px] tw-align-text-top" src="~/assets/images/header/close.png" /> -->
      <!-- <img class="vip-gift-modal-close" src="~/assets/icons/guanbi1.svg" /> -->
    </template>
    <div class="x-data-vip-box">
      <!-- 支付成功页面 -->
      <template v-if="orderInfo?.paySuccess">
        <!-- 团购 -->
        <div v-if="orderInfo.licenseNum > 1" class="x-data-vip-success-box-license">
          <div class="x-data-vip-success-box-license-scroll">
            <h3 class="x-data-vip-success-box-license-title">恭喜您购买成功！请按以下步骤分享给你的小伙伴吧！</h3>
            <div class="x-data-vip-success-box-license-content">
              <div class="x-data-vip-success-box-license-content-left">
                <img src="~/assets/icons/deli-module/share1.svg" />
                <span>1、在我的-邀请码找到"我的邀请码"</span>
                <span>2、复制发送给您的小伙伴</span>
                <span
                  >3、你的小伙伴注册得理法搜后，在<strong>我的</strong> / <strong>会员邀请码</strong> /
                  <strong>邀请码兑换</strong>进行验证</span
                >
              </div>
              <div class="x-data-vip-success-box-license-content-right">
                <img src="~/assets/icons/deli-module/share2.svg" />
                <span>1、在我的-邀请码找到"我的邀请码"</span>
                <span>2、分享给您的小伙伴</span>
                <span>3、你的小伙伴打开分享链接后注册领用</span>
              </div>
            </div>
            <div class="x-data-vip-success-box-license-btn">
              <a-button @click="handleCancel">继续使用</a-button>
              <a-button @click="handleExchange">前往兑换</a-button>
            </div>

            <div class="x-data-vip-success-box-license-tip">
              <span> 您可在个人中心-我的订单中开具发票 </span>
              <icon-font type="right-arrow-hn65ed58" @click="handleOpenInvoice" />
            </div>
            <a-divider />
            <div class="x-data-vip-success-box-license-footer">
              <span>购买方案：</span>
              <span>{{ orderInfo.planName }}</span>
              <span>团购人数：</span>
              <span>{{ orderInfo.licenseNum }}</span>
              <span>付款时间：</span>
              <span>{{ formatTime(orderInfo.payTime, "YYYY-MM-DD") }}</span>
              <span>订单编号：</span>
              <span>{{ orderInfo.payNo.slice(-15) }}</span>
              <span>付款方式：</span>
              <span>{{ formatPayment(orderInfo.payment) }}</span>
              <span>付款金额：</span>
              <span>{{ formatMoney(orderInfo.payAmount) }}</span>
            </div>
          </div>
        </div>
        <!-- 非团购 -->
        <div v-else class="x-data-vip-success-box">
          <div class="x-data-vip-success-box-scroll">
            <div class="x-data-vip-success-main">
              <span>购买方案：</span>
              <span>{{ orderInfo.planName }}</span>
              <span>订单编号：</span>
              <span>{{ orderInfo.payNo.slice(-15) }}</span>
              <span>付款时间：</span>
              <span>{{ formatTime(orderInfo.payTime, "YYYY-MM-DD") }}</span>
              <span>付款金额：</span>
              <span>{{ formatMoney(orderInfo.payAmount) }}</span>
              <span>付款方式：</span>
              <span>{{ formatPayment(orderInfo.payment) }}</span>
            </div>
            <a-button type="primary" @click="handleCancel">继续使用</a-button>
            <div class="x-data-vip-success-tip">
              <span>您可在个人中心-我的订单中开具发票</span>
              <icon-font type="right-arrow-hn65ed58" @click="handleOpenInvoice" />
            </div>
            <a-divider />
            <div class="x-data-vip-success-footer">
              <img src="~/assets/icons/deli-module/qr-code.png" />
              <div class="x-data-vip-success-footer-tip">
                <span>恭喜您，付款成功！打开微信扫描二维码</span>
                <span>添加客服，领取下单赠送会员！</span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- 支付页面 -->
      <template v-else>
        <main class="x-data-vip-box-main">
          <div class="x-data-vip-box-main-title-wrap">
            <template v-if="userInfo && expireInfo">
              <div class="x-data-vip-box-main-title-user">
                <img :src="userInfo.avatar || defaultAvatar" />
                <span class="x-data-vip-box-main-title-user-name">{{ userInfo.name }}</span>
                <span class="x-data-vip-box-main-title-user-date">{{ expireDate }}</span>
              </div>
              <!-- <img class="x-data-vip-box-main-title" src="~/assets/icons/deli-module/title.svg" /> -->
              <div ref="payModalRef">
                <a-tooltip
                  :align="{ points: ['tc', 'bc'], offset: [0, -8] }"
                  :get-popup-container="() => $refs.payModalRef"
                  overlay-class-name="customer-tips-overlay">
                  <div class="x-data-vip-box-main-title-customer" @mouseenter="handleCustomerServiceHover">
                    <img
                      class="x-data-vip-box-main-title-customer-icon"
                      src="~/assets/icons/deli-module/customer.svg" />
                    <span class="x-data-vip-box-main-title-customer-text">咨询专属客服</span>
                  </div>
                  <template #title>
                    <div>
                      <div class="customer-code-pine">
                        <img src="~/assets/icons/deli-module/coupon-code.png" class="qr-code" />
                        <span class="tip">添加客服领取试用</span>
                      </div>
                    </div>
                  </template>
                </a-tooltip>
              </div>
            </template>
            <template v-else>
              <div class="x-data-vip-box-main-title-user">
                <img src="~/assets/icons/deli-module/no-login-icon.svg" />
                <span class="x-data-vip-box-main-title-user-name">未登录</span>
                <span class="x-data-vip-box-main-title-user-date">--</span>
              </div>
              <div ref="payModalRef">
                <a-tooltip
                  :align="{ points: ['tc', 'bc'], offset: [0, -8] }"
                  :get-popup-container="() => $refs.payModalRef"
                  overlay-class-name="customer-tips-overlay">
                  <div class="x-data-vip-box-main-title-customer" @mouseenter="handleCustomerServiceHover">
                    <img
                      class="x-data-vip-box-main-title-customer-icon"
                      src="~/assets/icons/deli-module/customer.svg" />
                    <span class="x-data-vip-box-main-title-customer-text">咨询专属客服</span>
                  </div>
                  <template #title>
                    <div>
                      <div class="customer-code-pine">
                        <img src="~/assets/icons/deli-module/coupon-code.png" class="qr-code" />
                        <span class="tip">添加客服领取试用</span>
                      </div>
                    </div>
                  </template>
                </a-tooltip>
              </div>
            </template>
          </div>
          <div class="x-data-vip-box-main-container">
            <div class="x-data-vip-box-main-tabs" :style="{ backgroundImage: `url(${currentTabBg})` }">
              <button @click="onChangeType('VIP')"></button>
              <button @click="onChangeType('SVIP')"></button>
              <button @click="onChangeType('COMPANY')"></button>
            </div>
            <div class="x-data-vip-box-main-content">
              <!-- <template v-if="type == 'JIGOU'">
                <div class="x-data-vip-box-main-content-jigou-title">机构版专属,尊享加倍优惠</div>
                <div class="x-data-vip-box-main-content-jigou-sub-title">得理法律大模型“AI+律师”一站式服务平台</div>
                <div class="x-data-vip-box-main-content-jigou-desc"
                  >基于法律大模型开发的智慧法律系统&nbsp;&nbsp;为用户提供“AI+律师”的解决方案</div
                >
                <div class="x-data-vip-box-main-content-jigou-icons">
                  <div class="x-data-vip-box-main-content-jigou-icon">
                    <img src="~/assets/icons/deli-module/jigou-ai.svg" />
                    <span>小理AI</span>
                    <span>7*24智能法律专家</span>
                  </div>
                  <div class="x-data-vip-box-main-content-jigou-icon">
                    <img src="~/assets/icons/deli-module/jigou-data.svg" />

                    <span>得理法搜</span>
                    <span>法律难题就找得理法搜</span>
                  </div>
                  <div class="x-data-vip-box-main-content-jigou-icon">
                    <img src="~/assets/icons/deli-module/jigou-new.svg" />

                    <span>最新资讯</span>
                    <span>法律行业最具价值资讯</span>
                  </div>
                </div>
              </template> -->
              <template v-if="type === 'COMPANY'">
                <div class="company-vip-card">
                  <div
                    v-for="item in planList"
                    :key="item.planId"
                    class="company-vip-item"
                    :class="[
                      {
                        'company-vip-recommend': item.planId === 'COMPANY_l',
                        'company-vip-item-selected': item.planId === planId,
                      },
                    ]"
                    @click="onChangePlanId(item.planId)">
                    <!-- <icon-font
                      v-if="item.planId === 'COMPANY_l'"
                      class="company-vip-item-icon"
                      type="module-a-Slice541" />
                    <icon-font v-else class="company-vip-item-icon" type="module-a-Slice5411" /> -->
                    <div v-if="item?.leftTag" class="x-data-vip-box-main-plan-leftTag">{{ item?.leftTag }}</div>
                    <div v-if="item?.rightTag" class="x-data-vip-box-main-plan-rightTag">{{ item?.rightTag }}</div>
                    <div class="company-vip-box">
                      <div class="company-vip-right">{{ `企业会员·${item.planName}` }}</div>
                      <div class="company-vip-left">
                        <div class="company-vip-left-price">
                          <span class="company-vip-left-price-plan">{{ item.planPrice / 100 }}</span>
                          <span class="company-vip-left-price-source">¥{{ item.sourcePrice / 100 }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="x-data-vip-box-main-plans">
                  <div
                    v-for="(item, index) in planList"
                    :key="index"
                    class="x-data-vip-box-main-plan"
                    :class="{
                      'x-data-vip-box-main-plan-selected': planId === item.planId,
                      'x-data-vip-box-main-plan-discount':
                        (item?.couponTime && planId === item.planId) || item?.downTag,
                    }"
                    @click="onChangePlanId(item.planId)">
                    <div
                      class="x-data-vip-box-main-plan-pine"
                      :class="{ 'x-data-vip-box-main-plan-pine-selected': planId === item.planId }">
                      <template v-if="item?.couponTime">
                        <div v-if="item?.leftTag" class="x-data-vip-box-main-plan-leftTag">{{ item?.leftTag }}</div>
                        <div v-if="item?.rightTag" class="x-data-vip-box-main-plan-rightTag">{{ item?.rightTag }}</div>
                      </template>
                      <template v-else>
                        <div v-if="['l', 'm', 'c'].includes(item.planId)" class="x-data-vip-box-main-plan-tip">
                          限时优惠
                        </div>
                        <div v-if="['p'].includes(item.planId)" class="x-data-vip-box-main-plan-tip">推荐</div>
                      </template>

                      <div class="x-data-vip-box-main-plan-name">{{ item.planName }}</div>
                      <div class="x-data-vip-box-main-plan-planPrice">{{ item.planPrice / 100 }}</div>
                      <div v-if="item.sourcePrice" class="x-data-vip-box-main-plan-sourcePrice">
                        ¥{{ item.sourcePrice / 100 }}
                      </div>
                    </div>
                    <div v-if="item?.couponTime" class="x-data-vip-box-main-countdown">
                      <span v-if="item?.countdown && planId === item.planId">{{ item.countdown }}后失效</span>
                      <span v-else>{{ item?.downTag }}</span>
                    </div>
                  </div>
                </div>

                <div class="x-data-vip-box-main-month">
                  <!-- {{
                    planId == planInfo.planId ? `会员到期当天以${planInfo.payPrice / 100}元/月自动续费，可随时取消` : ""
                  }} -->
                  {{ curplan?.desc }}
                </div>
              </template>
              <div v-if="!['b', 'k'].includes(planId)" class="x-data-vip-box-main-number">
                <div class="x-data-vip-box-main-number-tip">选择人数：</div>
                <div class="x-data-vip-box-main-number-wrap">
                  <span>自定义：</span>
                  <div class="x-data-vip-box-main-number-wrap-input">
                    <button @click="handleReduceLicenseNum">
                      <icon-font type="Slice298" />
                    </button>
                    <div class="x-data-vip-box-main-number-wrap-input-content" @click="licenseNumInputRef.focus()">
                      <div class="x-data-vip-box-main-number-wrap-input-content-form">
                        <label id="user" class="input-box__label">{{ licenseNum }}</label>
                        <input
                          id="user"
                          ref="licenseNumInputRef"
                          v-model="licenseNum"
                          type="number"
                          @input="handleChangeLicenseNum" />
                      </div>
                      <span class="x-data-vip-box-main-number-wrap-input-tip">人</span>
                    </div>
                    <button @click="handleAddLicenseNum">
                      <icon-font type="Slice299" />
                    </button>
                  </div>
                  <div
                    v-if="planInfo"
                    class="x-data-vip-box-main-number-wrap-discount"
                    v-html="planInfo.discountDesc"></div>
                </div>
              </div>
              <img class="x-data-vip-box-main-tip" src="~/assets/icons/deli-module/tip.svg" />
              <img class="x-data-vip-box-main-table" src="~/assets/icons/deli-module/table.png" />
              <!-- <div class="lawyer-letter">
                <div class="lawyer-letter-title">律师函：</div>
                <div class="lawyer-letter-box">
                  <div class="lawyer-letter-item">
                    <div class="lawyer-letter-item-label">原价：</div>
                    <div class="lawyer-letter-item-content"> <strong style="color: #fb7005">488</strong> 元/次 </div>
                  </div>
                  <div class="lawyer-letter-item">
                    <div class="lawyer-letter-item-label">企业会员：</div>
                    <div class="lawyer-letter-item-content"> <strong style="color: #fb7005">388</strong> 元/次 </div>
                  </div>
                  <div class="lawyer-letter-item">
                    <div class="lawyer-letter-item-label">企业包年用户：</div>
                    <div class="lawyer-letter-item-content">
                      免费赠送 <strong style="color: #2051f3">1</strong> 次
                    </div>
                  </div>
                  <div class="lawyer-letter-item">
                    <div class="lawyer-letter-item-label">企业三年用户：</div>
                    <div class="lawyer-letter-item-content">
                      免费赠送 <strong style="color: #2051f3">5</strong> 次
                    </div>
                  </div>
                </div>
              </div>
              <div class="x-data-vip-box-main-table-tips">
                <div class="x-data-vip-box-main-table-tips-title">合同审查次数：</div>
                <div class="x-data-vip-box-main-table-tips-desc1">
                  包月用户每月<span class="vip-times">5</span>次，包年用户每年<span class="vip-times">60</span
                  >次，三年用户<span class="vip-times">200</span>次
                </div>
                <div class="x-data-vip-box-main-table-tips-desc2">
                  企业会员包年用户每年<span class="vip-times">120次</span>，三年用户<span class="vip-times">400次</span>
                </div>
              </div> -->
            </div>
          </div>
        </main>
        <aside class="x-data-vip-box-aside">
          <div class="x-data-vip-box-aside-title">付费及支付信息：</div>
          <template v-if="userInfo">
            <a-spin :spinning="qrcodeLoading">
              <a-config-provider :locale="locale === 'en' ? enUS : zhCN">
                <a-qrcode
                  v-if="qrCodeUrl && type !== 'JIGOU'"
                  :status="qrcodeStatus"
                  :value="qrCodeUrl"
                  @refresh="loadPlanInfo"
                  icon="https://delicdn.delilegal.com/dls/ad14e353cc5434dec2d209be9dd7ea82/ad14e353cc5434dec2d209be9dd7ea82.png" />
              </a-config-provider>

              <div v-if="type === 'JIGOU'" class="x-data-vip-box-aside-qrcode">
                <img src="~/assets/icons/deli-module/qr-code.png" />
              </div>
            </a-spin>
            <div class="x-data-vip-box-aside-pay-methods">
              <template v-if="type === 'JIGOU'">
                <icon-font type="Slice246" />
                <span class="desc">扫码联系客服</span>
              </template>
              <!-- 只有支付宝有连续包月 -->
              <template v-else-if="type !== 'JIGOU' && ['b', 'k'].includes(planId)">
                <icon-font type="Slice2451-hn664pnn" />
                <span class="desc">打开支付宝扫码支付</span>
              </template>
              <template v-else>
                <icon-font type="Slice2451-hn664pnn" />
                <icon-font type="Slice246" />
                <span class="desc">打开支付宝/微信扫码支付</span>
              </template>
            </div>
          </template>
          <div v-else class="x-data-vip-box-login-btn" @click="toLogin">立即登录</div>
          <template v-if="type === 'JIGOU'">
            <div class="x-data-vip-box-aside-phone">0755-26907610</div>
            <div class="x-data-vip-box-aside-phone-tip">
              <icon-font type="Slice273" />
              <span>电话联系客服</span>
            </div>
          </template>
          <template v-else>
            <div class="x-data-vip-box-aside-pay-info">
              <span>当前账户：</span>
              <span>{{ planInfo.currentUserName ?? "--" }}</span>
              <span>购买方案：</span>
              <span>{{ planInfo.planName ?? currentPlan?.planSubName ?? "--" }}</span>
              <span>实付金额：</span>
              <span class="x-data-vip-box-aside-pay-number" v-if="planInfo.payPrice">{{
                formatMoney(planInfo.payPrice)
              }}</span>
              <span class="x-data-vip-box-aside-pay-number" v-else>{{
                currentPlan?.planPrice ? formatMoney(currentPlan?.planPrice) : "--"
              }}</span>
            </div>
            <div v-if="planInfo?.couponName" class="x-data-vip-box-aside-coupon">
              <div class="x-data-vip-box-aside-coupon-title">
                <span class="x-data-vip-box-aside-coupon-tag">券</span>
                <span class="x-data-vip-box-aside-coupon-text"
                  >{{ planInfo?.couponName
                  }}<span class="x-data-vip-box-aside-coupon-text-expire"
                    >（还剩{{ planInfo?.couponExpireDay }}天）</span
                  ></span
                >
              </div>
              <span class="x-data-vip-box-aside-coupon-discount">{{ planInfo?.couponDesc }}</span>
            </div>
            <div v-else-if="planInfo.sourcePrice" class="x-data-vip-box-aside-coupon">
              <div class="x-data-vip-box-aside-coupon-title">
                <span class="x-data-vip-box-aside-coupon-tag">券</span>
                <span class="x-data-vip-box-aside-coupon-text">新客专享</span>
              </div>
              <span class="x-data-vip-box-aside-coupon-discount"
                >已减¥{{ (planInfo.sourcePrice / 100).toFixed(0) }}</span
              >
            </div>
            <div class="x-data-vip-box-aside-protocol">
              <template v-if="['b', 'k'].includes(planId)">
                <span>购买前请仔细阅读</span>
                <a :href="`${PROTOCOL_URL}/data/automaticRenewal.html`" target="_blank">《自动续费服务协议》</a>
              </template>
              <template v-else>
                <span>购买前请仔细阅读</span>
                <a :href="`${PROTOCOL_URL}/data/privacy.html`" target="_blank">《用户隐私及会员服务协议》</a>
              </template>
            </div>
            <div class="x-data-vip-box-aside-tip"> *支付完成后，可在个人中心-我的订单中 开具发票 </div>
          </template>
        </aside>
      </template>
    </div>
    <!-- 离开二次确认弹窗 -->
    <a-modal
      v-model:open="confirmModal"
      wrap-class-name="x-data-vip-confirm-modal"
      :keyboard="false"
      :mask-closable="false"
      :footer="null"
      :centered="true"
      @cancel="handleKeep"
      @ok="handleKeep">
      <template #closeIcon>
        <icon-font type="module-a-Slice267" />
      </template>
      <div class="x-data-vip-confirm-box">
        <div class="x-data-vip-confirm-main">
          <div class="x-data-vip-confirm-main-left">
            {{ `${type === "COMPANY" ? "企业版" : type}·${planInfo.planName}` }}
          </div>
          <div class="x-data-vip-confirm-main-right">
            <span>¥{{ formatMoney(planInfo.payPrice) }}</span>
            <span>¥{{ formatMoney(sourcePrice * licenseNum) }}</span>
            <span>已减¥{{ formatMoney(sourcePrice * licenseNum - planInfo.payPrice) }}</span>
          </div>
        </div>
        <div class="x-data-vip-confirm-footer">
          <a-button @click="handleLeave">放弃特权</a-button>
          <a-button @click="handleKeep">继续开通</a-button>
        </div>
      </div>
    </a-modal>

    <!-- 新用户挽留弹窗 -->
    <a-modal
      v-model:open="showRetainPopup"
      wrap-class-name="x-data-vip-retain-modal"
      :keyboard="false"
      :mask-closable="false"
      :footer="null"
      :centered="true"
      :closable="false"
      @cancel="closeRetainPopup">
      <div class="x-data-vip-retain-modal-container1" v-if="registerDay > 3 && registerDay < 31">
        <div class="x-data-vip-retain-modal-footer">
          <div class="x-data-vip-retain-modal-footer-btn cancel" @click="closeRetain">放弃特权</div>
          <div
            class="x-data-vip-retain-modal-footer-btn confirm"
            v-logger-track="'首购加赠时长挽留弹窗_立即使用'"
            @click="closeRetainPopup">
            立即开通
          </div>
        </div>
      </div>
      <div class="x-data-vip-retain-modal-container2" v-else-if="registerDay > 0">
        <div class="amcount">
          <span class="num">50</span>
          <span class="unit">元</span>
        </div>
        <div class="x-data-vip-retain-modal-footer">
          <div class="x-data-vip-retain-modal-footer-btn cancel" @click="closeRetain">放弃特权</div>
          <div
            class="x-data-vip-retain-modal-footer-btn confirm"
            v-logger-track="'首购50元优惠挽留弹窗_立即使用'"
            @click="closeRetainPopup">
            立即开通
          </div>
        </div>
      </div>
    </a-modal>

    <CouponModal
      v-if="showCouponPopup"
      v-model="showCouponPopup"
      :picture="ModalConfig?.pictureUrl"
      :type="ModalConfig?.type"
      :time="ModalConfig?.expireTime"
      @on-close="showCouponPopup = false" />
  </a-modal>
</template>

<style lang="less">
  .x-data-vip-modal,
  .x-data-vip-confirm-modal {
    div[class^="ant-modal"] {
      width: fit-content !important;
      padding: 0;
      background-color: transparent !important;
      box-shadow: none;
    }

    .ant-modal-content {
      padding-right: 56px !important;
    }

    .ant-modal-close-x {
      width: 40px;
      height: 40px;

      .anticon {
        font-size: 40px;
      }
    }
  }

  .x-data-vip-box {
    --width: fit-content;
    --flex-direction: row;
    --main-border-radius: 16px 0 0 16px;
    --aside-border-radius: 0 16px 16px 0;
    --success-license-flex-direction: row;
    --success-license-align-items: flex-start;
    --success-template-columns: repeat(2, auto 1fr);
    --success-grid-justify-content: flex-start;
    --success-footer-justify-content: flex-start;
    --success-footer-flex-wrap: nowrap;
    --plans-flex-justify-content: flex-start;

    display: flex;
    flex-direction: var(--flex-direction);
    gap: 20px 0;
    align-items: center;
    justify-content: center;
    width: var(--width);
    min-width: calc(320px - 56px);
    max-width: 990px;
    min-height: 660px;
    border-radius: 16px;

    @media (max-width: 1024px) {
      --width: min-content;
      --flex-direction: column;
      --main-border-radius: 16px;
      --aside-border-radius: 16px;
    }

    @media (max-width: 768px) {
      --success-footer-justify-content: center;
      --success-footer-flex-wrap: wrap;
      --success-template-columns: repeat(2, auto);
      --success-grid-justify-content: center;
      --confirm-flex-direction: column;
      --confirm-justify-content: center;
    }

    @media (max-width: 480px) {
      --success-template-columns: auto;
      --success-grid-justify-content: center;
      --success-license-flex-direction: column;
      --success-license-align-items: center;
      --plans-flex-justify-content: center;
    }

    .x-data-vip-success-box-license {
      width: calc(100vw - 86px);
      min-width: calc(320px - 56px);
      max-width: 990px;
      height: 660px;
      background-color: #fff;
      border-color: transparent;
      border-style: solid;
      border-width: 202px 6.35vw 48px;
      border-radius: 50px;
      border-image: url("~/assets/icons/deli-module/success-bg.svg");
      border-image-slice: 202 122 48 122;
      border-image-width: 202px 6.35vw 48px;

      .x-data-vip-success-box-license-scroll {
        width: 100%;
        height: 100%;
        overflow-y: auto;

        &::-webkit-scrollbar {
          display: none;
        }

        .x-data-vip-success-box-license-title {
          margin: 0;
          margin-bottom: 24px;
          font-size: 22px;
          line-height: 26px;
          color: #333;
        }

        .x-data-vip-success-box-license-content {
          display: flex;
          flex-direction: var(--success-license-flex-direction);
          gap: 40px;
          align-items: var(--success-license-align-items);
          font-size: 20px;
          font-weight: 400;
          color: #333;

          img {
            width: 148px;
            height: 45px;
          }

          .x-data-vip-success-box-license-content-left,
          .x-data-vip-success-box-license-content-right {
            display: flex;
            flex: 1;
            flex-direction: column;
            gap: 20px 0;
          }
        }

        .x-data-vip-success-box-license-btn {
          display: flex;
          flex-wrap: wrap;
          gap: 16px 82px;
          align-items: center;
          justify-content: center;

          .ant-btn {
            width: 198px;
            height: 56px;
            margin-top: 24px;
            margin-bottom: 16px;
            font-size: 26px;
            line-height: 30px;
            color: #696969;
            background: #ececec;
            border: none !important;
            border-radius: 12px;

            &:last-of-type {
              color: #fff;
              background: #3957fe;
            }
          }
        }

        .x-data-vip-success-box-license-tip {
          display: flex;
          gap: 2px;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          line-height: 23px;
          color: #a1a1a1;

          .anticon {
            font-size: 28px;
            cursor: pointer;
          }
        }

        .ant-divider {
          margin: 24px 0;
        }

        .x-data-vip-success-box-license-footer {
          display: grid;
          grid-template-columns: var(--success-template-columns);
          gap: 24px 40px;
          justify-content: var(--success-footer-justify-content);
          width: 100%;
          margin-bottom: 48px;

          span {
            &:nth-of-type(odd) {
              font-size: 20px;
              line-height: 23px;
              color: #696969;
            }

            &:nth-of-type(even) {
              font-size: 20px;
              line-height: 23px;
              color: #333;
            }
          }
        }
      }
    }

    .x-data-vip-success-box {
      width: calc(100vw - 86px);
      min-width: calc(320px - 56px);
      max-width: 990px;
      height: 660px;
      background-color: #fff;
      border-color: transparent;
      border-style: solid;
      border-width: 202px 6.35vw 48px;
      border-radius: 50px;
      border-image: url("~/assets/icons/deli-module/success-bg.svg");
      border-image-slice: 202 122 48 122;
      border-image-width: 202px 6.35vw 48px;

      .x-data-vip-success-box-scroll {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        height: 100%;
        overflow-y: auto;

        &::-webkit-scrollbar {
          display: none;
        }

        .x-data-vip-success-main {
          display: grid;
          grid-template-columns: var(--success-template-columns);
          gap: 24px 0;
          justify-content: var(--success-grid-justify-content);
          width: 100%;

          span {
            &:nth-of-type(odd) {
              font-size: 20px;
              line-height: 23px;
              color: #696969;
            }

            &:nth-of-type(even) {
              font-size: 20px;
              line-height: 23px;
              color: #333;
            }
          }
        }

        .ant-btn {
          width: 198px;
          height: 56px;
          margin-top: 24px;
          margin-bottom: 16px;
          font-size: 26px;
          line-height: 30px;
          color: #fff;
          background-color: #3957fe;
          border: none !important;
          border-radius: 12px;
        }

        .x-data-vip-success-tip {
          display: flex;
          gap: 2px;
          align-items: center;
          font-size: 20px;
          line-height: 23px;
          color: #a1a1a1;

          .anticon {
            font-size: 28px;
            cursor: pointer;
          }
        }

        .ant-divider {
          margin: 24px 0;
        }

        .x-data-vip-success-footer {
          display: flex;
          flex-wrap: var(--success-footer-flex-wrap);
          gap: 16px;
          align-items: center;
          justify-content: var(--success-footer-justify-content);
          width: 100%;

          img {
            grid-area: qrcode;
            width: 80px;
            height: 80px;
          }

          .x-data-vip-success-footer-tip {
            display: flex;
            flex-direction: column;
            gap: 16px;

            span {
              font-size: 20px;
              line-height: 23px;
              color: #a1a1a1;
            }
          }
        }
      }
    }

    .x-data-vip-box-main {
      display: flex;
      flex-direction: column;
      width: calc(100vw - 86px);
      min-width: calc(320px - 56px);
      max-width: 706px;
      height: 660px;
      padding: 20px;
      background-image: url("~/assets/icons/deli-module/main-bg.svg");
      background-repeat: no-repeat;
      background-size: 100% 100%;
      border-radius: var(--main-border-radius);

      .x-data-vip-box-main-title-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 16px 0;
        align-items: center;
        justify-content: space-between;
        margin-top: 6px;
        margin-bottom: 26px;

        .x-data-vip-box-main-title-user {
          display: grid;
          grid-template-areas: "avatar name" "avatar date";
          gap: 3px 12px;

          img {
            grid-area: avatar;
            width: 48px;
            height: 48px;
            border-radius: 50%;
          }

          .x-data-vip-box-main-title-user-name {
            grid-area: name;
            font-size: 18px;
            line-height: 21px;
            color: #683510;
          }

          .x-data-vip-box-main-title-user-date {
            grid-area: date;
            font-size: 14px;
            line-height: 16px;
            color: #683510;
          }
        }

        .x-data-vip-box-main-title-customer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 146px;
          height: 48px;
          padding: 0 12px;
          background: #f8e7cb;
          border: 1px solid #fff0d8;
          border-radius: 24px;

          .x-data-vip-box-main-title-customer-icon {
            width: 24px;
            height: 24px;
          }

          .x-data-vip-box-main-title-customer-text {
            font-size: 14px;
            color: #c28c5d;
          }
        }

        .x-data-vip-box-main-title {
          max-width: 100%;
          height: 23px;
        }
      }

      .x-data-vip-box-main-container {
        display: flex;
        flex: 1;
        flex-direction: column;
        height: 0;
        background-image: linear-gradient(#fff, #fff);
        background-repeat: no-repeat;
        background-position: 0 50px;
        background-size: 100% 100%;

        .x-data-vip-box-main-tabs {
          display: flex;
          width: 100%;
          height: 50px;
          background-color: #fbeecf;
          background-repeat: no-repeat;
          background-size: 100% 100%;
          border-top-left-radius: 14px;
          border-top-right-radius: 14px;

          button {
            flex: 1;
            width: 0;
            height: 100%;
            cursor: pointer;
            background: transparent;
            border: 0;
          }
        }

        .x-data-vip-box-main-content {
          flex: 1;
          padding: 0 20px 20px;
          padding-top: 8px;
          margin-top: 24px;
          overflow-y: auto;
          background: #fff;
          border-bottom-right-radius: 16px;
          border-bottom-left-radius: 16px;

          &::-webkit-scrollbar {
            display: none;
          }

          .company-vip-card {
            display: flex;
            gap: 14px;
            padding-bottom: 20px;

            .company-vip-item {
              position: relative;
              box-sizing: content-box;
              display: flex;
              flex-direction: column;
              justify-content: center;
              width: 306px;
              height: 126px;
              cursor: pointer;
              background: #fdf9eb;
              border: 1px solid #e5cca4;
              border-radius: 12px;

              .x-data-vip-box-main-plan-leftTag {
                position: absolute;
                top: -6px;
                left: 0;
                padding: 4px;
                font-size: 12px;
                color: #a18b69;
                background: #ffecc8;
                border-radius: 11px 0;
              }

              .x-data-vip-box-main-plan-rightTag {
                position: absolute;
                top: -8px;
                right: -4px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 34px;
                height: 34px;
                font-size: 8px;
                font-weight: 500;
                color: #a18b69;
                text-align: center;
                white-space: normal;
                background: url("~/assets/icons/deli-module/pay-right-tag-active.png");
                background-size: cover;
              }

              &.company-vip-recommend {
                .company-vip-item-icon {
                  top: -11px;
                  font-size: 44px;
                }

                .company-vip-box {
                  .company-vip-left {
                    .company-vip-left-price {
                      .company-vip-left-price-plan {
                        color: #ed6425;
                      }
                    }
                  }
                }
              }

              &.company-vip-item-selected {
                background-image: url("~/assets/icons/deli-module/company-bg.svg");
                background-repeat: no-repeat;
                background-size: 100% 100%;
                border: 1px solid #ce854a;

                .x-data-vip-box-main-plan-rightTag {
                  color: #fff;
                  background: url("~/assets/icons/deli-module/pay-right-tag-active.png");
                  background-size: cover;
                }

                .x-data-vip-box-main-plan-leftTag {
                  color: #fff;
                  background: linear-gradient(117deg, #ce854a 0%, #f8bd57 100%);
                }
              }

              .company-vip-item-icon {
                position: absolute;
                top: -23px;
                left: 0;
                font-size: 66px;
              }

              .company-vip-box {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0 20px;

                .company-vip-right {
                  font-size: 16px;
                  font-weight: bold;
                  color: #18173e;
                }

                .company-vip-left {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                  justify-content: center;

                  .company-vip-left-price {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;

                    .company-vip-left-price-plan {
                      font-size: 28px;
                      color: #683510;

                      &::before {
                        font-size: 16px;
                        content: "¥";
                      }
                    }

                    .company-vip-left-price-source {
                      font-size: 13px;
                      color: #b1682c;
                      text-decoration: line-through;

                      &::before {
                        text-decoration: none;
                        content: "原价：";
                      }

                      &::after {
                        text-decoration: none;
                        content: "/年";
                      }
                    }
                  }
                }
              }
            }
          }

          .x-data-vip-box-main-plans {
            display: flex;
            flex-wrap: wrap;
            gap: 14px;
            justify-content: var(--plans-flex-justify-content);
            padding-bottom: 24px;

            .x-data-vip-box-main-plan {
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 146px;
              height: 126px;
              cursor: pointer;
              background: #fdf9eb;
              border: 1px solid #e5cca4;
              border-radius: 12px;

              .x-data-vip-box-main-plan-pine {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                height: 107px;
                cursor: pointer;
                background: #fdf9eb;
                // border: 1px solid #e5cca4;
                border-radius: 12px;
              }

              .x-data-vip-box-main-countdown {
                width: 100%;
                font-size: 12px;
                color: #fff;
                text-align: center;
              }

              .x-data-vip-box-main-plan-pine-selected {
                background: linear-gradient(135deg, #fdf9eb 0%, #f5daa8 100%);
              }

              .x-data-vip-box-main-plan-tip {
                position: absolute;
                top: 0;
                left: 0;
                padding: 4px;
                font-size: 12px;
                color: #fff;
                background: #ed6425;
                //background: linear-gradient(117deg, #ce854a 0%, #f8bd57 100%);
                border-radius: 11px 0;
              }

              .x-data-vip-box-main-plan-leftTag {
                position: absolute;
                top: -6px;
                left: 0;
                padding: 4px;
                font-size: 12px;
                // color: #a18b69;
                // background: #ffecc8;
                color: #fff;
                background: linear-gradient(117deg, #ce854a 0%, #f8bd57 100%);
                border-radius: 11px 0;
              }

              .x-data-vip-box-main-plan-rightTag {
                position: absolute;
                top: -8px;
                right: -4px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 34px;
                height: 34px;
                font-size: 8px;
                font-weight: 500;
                color: #fff;
                text-align: center;
                white-space: normal;
                // background: url("~/assets/icons/deli-module/pay-right-tag.png");
                background: url("~/assets/icons/deli-module/pay-right-tag-active.png");
                background-size: cover;
              }

              .x-data-vip-box-main-plan-name {
                margin-top: 22px;
                font-size: 15px;
                line-height: 18px;
                color: #683510;
              }

              .x-data-vip-box-main-plan-planPrice {
                margin-bottom: 2px;
                font-size: 30px;
                line-height: 35px;
                color: #ed6425;

                &::before {
                  font-size: 18px;
                  line-height: 21px;
                  color: #ed6425;
                  content: "¥";
                }
              }

              .x-data-vip-box-main-plan-sourcePrice {
                font-size: 13px;
                line-height: 15px;
                color: #b1682c;
                text-decoration: line-through;
              }
            }

            .x-data-vip-box-main-plan-selected {
              background: linear-gradient(135deg, #fdf9eb 0%, #f5daa8 100%);
              border: 1px solid #ce854a;

              .x-data-vip-box-main-plan-rightTag {
                color: #fff;
                background: url("~/assets/icons/deli-module/pay-right-tag-active.png");
                background-size: cover;
              }

              .x-data-vip-box-main-plan-leftTag {
                color: #fff;
                background: linear-gradient(117deg, #ce854a 0%, #f8bd57 100%);
              }
            }

            .x-data-vip-box-main-plan-discount {
              background: linear-gradient(90deg, #f0844b 0%, #ec463a 100%);
            }
          }

          .x-data-vip-box-main-number {
            .x-data-vip-box-main-number-tip {
              margin-bottom: 16px;
              font-size: 16px;
              line-height: 19px;
              color: #683510;
            }

            .x-data-vip-box-main-number-wrap {
              display: flex;
              flex-wrap: wrap;
              gap: 16px 0;
              align-items: center;

              .x-data-vip-box-main-number-wrap-discount {
                font-size: 16px;
                line-height: 19px;
                color: #683510;

                &:last-of-type {
                  margin-left: 8px;
                }
              }

              .x-data-vip-box-main-number-wrap-input {
                display: flex;
                width: 168px;
                height: 36px;
                background: #fdf9eb;
                border: 1px solid #e5cca4;
                border-radius: 12px;

                button {
                  display: flex;
                  align-items: center;
                  align-self: center;
                  justify-content: center;
                  width: 36px;
                  height: 36px;
                  padding: 0;
                  cursor: pointer;
                  background: transparent;
                  border: 0;

                  .anticon {
                    font-size: 16px;
                  }

                  &:first-of-type {
                    border-right: 1px solid #e5cca4;
                  }

                  &:last-of-type {
                    border-left: 1px solid #e5cca4;
                  }
                }

                .x-data-vip-box-main-number-wrap-input-content {
                  display: flex;
                  flex: 1;
                  align-items: baseline;
                  justify-content: center;
                  width: 0;

                  .x-data-vip-box-main-number-wrap-input-content-form {
                    position: relative;
                    font-size: 23px;
                    line-height: 27px;
                    color: #683510;
                    text-align: end;

                    label {
                      width: fit-content;
                      visibility: hidden;
                    }

                    input {
                      position: absolute;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 100%;
                      padding: 0;
                      background: transparent;
                      border: none;

                      &::-webkit-outer-spin-button,
                      &::-webkit-inner-spin-button {
                        appearance: none;
                      }

                      &[type="number"] {
                        appearance: textfield;
                      }
                    }
                  }

                  .x-data-vip-box-main-number-wrap-input-tip {
                    font-size: 14px;
                    line-height: 36px;
                    color: #683510;
                  }
                }
              }
            }
          }

          .x-data-vip-box-main-month {
            font-size: 14px;
            line-height: 16px;
            color: #683510;
          }

          .x-data-vip-box-main-tip {
            width: 100%;
            height: 28px;
            margin: 24px 0;
          }

          .x-data-vip-box-main-table {
            width: 100%;
          }

          .lawyer-letter {
            .lawyer-letter-title {
              margin-top: 24px;
              margin-bottom: 12px;
              margin-left: 16px;
              font-size: 16px;
              color: #8b8b93;
            }

            .lawyer-letter-box {
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
              width: 100%;
              height: 88px;
              padding: 16px;
              background: #fefefd;
              border: 1px solid #f1f1f1;
              border-radius: 10px;

              .lawyer-letter-item {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                width: calc(50% - 16px);

                .lawyer-letter-item-label {
                  font-size: 16px;
                  color: #af997a;
                }

                .lawyer-letter-item-content {
                  font-size: 16px;
                  color: #3c3b5d;
                }
              }
            }
          }

          .x-data-vip-box-main-table-tips {
            margin-left: 16px;

            .x-data-vip-box-main-table-tips-title {
              margin-top: 24px;
              margin-bottom: 12px;
              font-size: 16px;
              color: #8b8b93;
            }

            .x-data-vip-box-main-table-tips-desc1,
            .x-data-vip-box-main-table-tips-desc2 {
              margin-bottom: 8px;
              font-size: 16px;
              color: #3c3b5d;

              .vip-times {
                font-weight: 600;
                color: #fbbc05;
              }
            }
          }

          //---- 机构版 ----
          .x-data-vip-box-main-content-jigou-title {
            margin-top: 99px;
            margin-bottom: 36px;
            font-size: 30px;
            font-weight: bold;
            color: #f59221;
            text-align: center;
          }

          .x-data-vip-box-main-content-jigou-sub-title {
            margin-bottom: 6px;
            font-size: 24px;
            line-height: 28px;
            color: #000;
            text-align: center;
          }

          .x-data-vip-box-main-content-jigou-desc {
            margin-bottom: 16px;
            font-size: 18px;
            line-height: 21px;
            color: #666;
            text-align: center;
          }

          .x-data-vip-box-main-content-jigou-icons {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-evenly;

            .x-data-vip-box-main-content-jigou-icon {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;

              .img {
                width: 48px;
                height: 48px;
                margin-bottom: 8px;
              }

              span {
                text-align: center;

                &:first-of-type {
                  margin-bottom: 8px;
                  font-size: 18px;
                  line-height: 21px;
                  color: #000;
                }

                &:last-of-type {
                  font-size: 16px;
                  line-height: 19px;
                  color: #666;
                }
              }
            }
          }
        }
      }
    }

    .x-data-vip-box-aside {
      width: 284px;
      height: 660px;
      padding: 38px 20px 20px;
      background-image: url("~/assets/icons/deli-module/aside-bg.svg");
      background-repeat: no-repeat;
      background-size: contain;
      border-radius: var(--aside-border-radius);

      .x-data-vip-box-aside-title {
        margin-bottom: 106px;
        font-size: 14px;
        line-height: 16px;
        color: #ab6213;
      }

      .x-data-vip-box-login-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 244px;
        height: 48px;
        margin-bottom: 158px;
        font-size: 16px;
        font-weight: 600;
        color: white;
        cursor: pointer;
        background: linear-gradient(90deg, #edaa25 0%, #ed6425 100%);
        border-radius: 16px;
      }

      .ant-spin-nested-loading {
        width: 146px;
        height: 146px;
        margin: 0 auto;
        margin-bottom: 12px;

        .x-data-vip-box-aside-qrcode {
          width: 146px;
          height: 146px;
          padding: 14px;
          background: #fff;
          border: 1px solid #b39986;
          border-radius: 12px;

          .ant-spin-blur {
            opacity: 0.1;
          }

          img {
            width: 100%;
            height: 100%;
          }
        }

        .ant-qrcode {
          display: block !important;
          width: 100% !important;
          height: 100% !important;
          padding: 14px !important;
          background: #fff !important;
          border: 1px solid #b39986;
          border-radius: 12px;

          canvas {
            width: 100% !important;
            height: 100% !important;
          }
        }
      }

      .x-data-vip-box-aside-pay-methods {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;
        margin-bottom: 28px;
        font-size: 14px;
        line-height: 16px;
        color: #67340e;

        .anticon {
          font-size: 18px;
        }
      }

      .x-data-vip-box-aside-pay-info {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: flex-end;

        span {
          margin-bottom: 24px;

          &:nth-of-type(odd) {
            font-size: 14px;
            line-height: 16px;
            color: #ab6213;
            white-space: nowrap;
          }

          &:nth-of-type(even) {
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            color: #67340e;
          }
        }

        .x-data-vip-box-aside-pay-number {
          font-size: 36px !important;
          line-height: 0;
          color: #ed6425 !important;

          &::before {
            font-size: 20px;
            line-height: 23px;
            content: "¥";
          }
        }
      }

      .x-data-vip-box-aside-coupon {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 30px;
        padding: 0 6px;
        margin-bottom: 8px;
        background: #fff;
        border: 1px solid #fffbf3;
        border-radius: 6px;

        .x-data-vip-box-aside-coupon-title {
          display: flex;
          align-items: center;
          justify-content: center;

          .x-data-vip-box-aside-coupon-tag {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            margin-right: 6px;
            font-size: 13px;
            color: #fff;
            background: linear-gradient(315deg, #f56a37 0%, #fc9b44 100%);
            border-radius: 4px;
          }

          .x-data-vip-box-aside-coupon-text {
            font-size: 13px;
            color: #67340e;
          }
        }

        .x-data-vip-box-aside-coupon-discount {
          font-size: 13px;
          color: #ed6425;
        }

        .x-data-vip-box-aside-coupon-text-expire {
          font-size: 13px;
          color: #d8d8d8;
        }
      }

      .x-data-vip-box-aside-protocol {
        margin-bottom: 36px;
        font-size: 14px;
        line-height: 16px;

        span {
          color: #ab6213;
        }

        a {
          color: #67340e;
          text-decoration: underline;
        }
      }

      // ---- 机构版 ----
      .x-data-vip-box-aside-phone {
        margin-bottom: 13px;
        font-size: 20px;
        line-height: 23px;
        color: #1f1f1f;
        text-align: center;
      }

      .x-data-vip-box-aside-phone-tip {
        display: flex;
        gap: 7px;
        align-items: center;
        justify-content: center;

        .anticon {
          font-size: 18px;
        }

        span {
          font-size: 14px;
          line-height: 16px;
          color: #67340e;
        }
      }
    }
  }

  .x-data-vip-confirm-box {
    --confirm-flex-direction: row;
    --confirm-justify-content: space-between;

    display: flex;
    flex-direction: column;
    align-items: center;
    width: calc(100vw - 86px);
    min-width: calc(320px - 56px);
    max-width: 594px;
    min-height: 576px;
    background-image: linear-gradient(#fff, #fff);
    background-repeat: no-repeat;
    background-size: 100%;
    border-color: transparent;
    border-style: solid;
    border-width: 343px 3.13vw 48px;
    border-image: url("~/assets/icons/deli-module/confirm-bg.svg");
    border-image-slice: 343 60 48 60;
    border-image-width: 343px 3.13vw 48px;

    @media (max-width: 768px) {
      --confirm-flex-direction: column;
      --confirm-justify-content: center;
    }

    .x-data-vip-confirm-main {
      display: flex;
      flex-flow: var(--confirm-flex-direction) wrap;
      align-items: center;
      justify-content: var(--confirm-justify-content);
      width: 100%;
      min-height: 72px;
      padding: 15px 16px;
      margin-bottom: 64px;
      background: linear-gradient(90deg, #ff8806 0%, #fe5638 100%);
      border-radius: 16px;

      .x-data-vip-confirm-main-left {
        font-size: 30px;
        line-height: 35px;
        color: #fff;
      }

      .x-data-vip-confirm-main-right {
        display: flex;
        align-items: baseline;

        span {
          &:first-of-type {
            font-size: 30px;
            line-height: 35px;
            color: #fff;
          }

          &:nth-of-type(2) {
            font-size: 18px;
            line-height: 21px;
            color: #fff;
            text-decoration: line-through;
          }

          &:last-of-type {
            padding: 3px 8px;
            font-size: 16px;
            line-height: 19px;
            color: #ff642c;
            background-color: white;
            border-radius: 6px;
          }
        }
      }
    }

    .x-data-vip-confirm-footer {
      display: flex;
      flex-wrap: wrap;
      gap: 16px 36px;
      align-items: center;
      justify-content: center;

      .ant-btn {
        width: 168px;
        height: 56px;
        font-size: 18px;
        line-height: 21px;
        color: #a9a9a9;
        border-radius: 30px;

        &:first-of-type {
          background: #fff;
          border: 1px solid #dcdcdc;
        }

        &:last-of-type {
          color: #fff;
          background: #fba505;
          border: 1px solid #fba505;
        }
      }
    }
  }

  .x-data-vip-retain-modal {
    div[class^="ant-modal"] {
      width: fit-content !important;
      padding: 0;
      background-color: transparent !important;
      box-shadow: none;
    }

    &-container1 {
      position: relative;
      width: 305px;
      height: 380px;
      background: url("~/assets/icons/deli-module/retain-bg1.png");
      background-size: cover;

      .x-data-vip-retain-modal-footer {
        position: absolute;
        bottom: 15px;
      }
    }

    &-container2 {
      position: relative;
      width: 305px;
      height: 380px;
      background: url("~/assets/icons/deli-module/retain-bg2.png");
      background-size: cover;

      .x-data-vip-retain-modal-footer {
        position: absolute;
        bottom: 20px;
      }

      .amcount {
        position: absolute;
        bottom: 104px;
        left: 173px;
        display: flex;
        align-items: center;

        .num {
          margin-right: 6px;
          font-size: 32px;
          font-weight: bold;
          color: #fe5c18;
        }

        .unit {
          font-size: 20px;
          font-weight: bold;
          color: #fe5c18;
        }
      }
    }

    &-footer {
      display: flex;
      gap: 17px;
      justify-content: center;
      width: 100%;

      &-btn {
        width: 120px;
        height: 42px;
        font-size: 17px;
        line-height: 42px;
        text-align: center;
        cursor: pointer;
        border-radius: 24px;
      }

      .confirm {
        font-weight: 500;
        color: white;
        background: linear-gradient(92deg, #fb6e33 0%, #fcad73 100%);
      }

      .cancel {
        color: #9f8c6c;
        background: #fff;
        border: 1px solid #e8cbab;
      }
    }
  }
</style>

<style scoped lang="less">
  :deep(.customer-tips-overlay) {
    // width: 198px;
    width: 146px;
    height: 168px;
    background: #fff;
    border-radius: 24px;
    box-shadow: 4px 4px 40px 0 #e8d6b7;

    &.ant-tooltip {
      max-width: 35rem !important;
      padding: 0;
    }

    .ant-tooltip-inner {
      padding: 0 !important;
      background: none !important;
      box-shadow: none !important;
    }

    .ant-tooltip-arrow {
      display: none;

      &::before {
        background: #fff;
      }
    }

    .customer-code-pine {
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: center;
      justify-content: center;
      width: 146px;
      height: 168px;

      .qr-code {
        width: 106px;
        height: 106px;
      }

      .tip {
        font-size: 14px;
        color: #969696;
      }
    }
  }
</style>
